import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef, useLayoutEffect, useCallback } from "react";
import classNames from "classnames/bind";

import {
  CalendarIcon,
  CalendarActiveIcon,
  DashboardIcon,
  DashboardActiveIcon,
  DocumentIcon,
  DocumentActiveIcon,
  ListIcon,
  ListActiveIcon,
  NotesIcon,
  NotesActiveIcon,
  SettingIcon,
  SettingActiveIcon,
  NotificationIcon,
  MessageIcon,
  SearchIcon,
  PlusIcon,
  LogOutIcon,
  NoteIcon,
  NoteActiveIcon,
  NoteNotificationIcon,
} from "~/assets/icons";
import { BaseImage } from "~/components/common";
import logo from "~/assets/images/CBR Logo.svg";
import styles from "./Header.module.scss";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { authActions } from "~/thunks/auth/authSlice";
import { jobActions } from "~/thunks/job/jobSlice";
import { handleCreateAddJob, handleGetJobs } from "~/thunks/job/jobThunk";
import { adminRouteAbsolute } from "~/utils/constants/route";
import { StorageEnum } from "~/utils/enum";
import accountApi from "~/apis/account";
import { IResponseGetNotifications, INotifications } from "~/utils/interface/account";
import AddJobModal from "~/pages/admin/jobs/add-job/AddJobModal";
import { clearAddJobDraft } from "~/pages/admin/jobs/add-job/addJobDraft";
import { notify } from "~/utils/helper";

const cx = classNames.bind(styles);

const sidebarList = [
  {
    name: "dashboard",
    icon: <DashboardIcon className={cx("icon-nav-menu")} />,
    activeIcon: <DashboardActiveIcon className={cx("icon-nav-menu")} />,
    url: "/admin/dashboard",
  },
  {
    name: "jobs",
    icon: <ListIcon className={cx("icon-nav-menu")} />,
    activeIcon: <ListActiveIcon className={cx("icon-nav-menu")} />,
    url: "/admin/job",
  },
  {
    name: "calendar",
    icon: <CalendarIcon className={cx("icon-nav-menu")} />,
    activeIcon: <CalendarActiveIcon className={cx("icon-nav-menu")} />,
    url: "/admin/calendar",
  },
  {
    name: "crm",
    icon: <NoteIcon className={cx("icon-nav-menu")} />,
    activeIcon: <NoteActiveIcon className={cx("icon-nav-menu")} />,
    url: adminRouteAbsolute.crm.main,
  },
  {
    name: "communication",
    icon: <MessageIcon className={cx("icon-nav-menu")} />,
    activeIcon: <MessageIcon className={cx("icon-nav-menu")} />,
    url: "/admin/communication",
  },
  {
    name: "payments",
    icon: <DocumentIcon className={cx("icon-nav-menu")} />,
    activeIcon: <DocumentActiveIcon className={cx("icon-nav-menu")} />,
    url: "/admin/payment",
  },
  {
    name: "reports",
    icon: <NotesIcon className={cx("icon-nav-menu")} />,
    activeIcon: <NotesActiveIcon className={cx("icon-nav-menu")} />,
    url: "/admin/report",
  },
  {
    name: "settings",
    icon: <SettingIcon className={cx("icon-nav-menu")} />,
    activeIcon: <SettingActiveIcon className={cx("icon-nav-menu")} />,
    url: "/admin/setting",
  },
];

export default function Header() {
  const { t } = useTranslation(["admin/layout"]);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showAddJobModal } = useAppSelector((state) => state.job);
  const [activeScreen, setActiveScreen] = useState<string>("dashboard");
  const [indicatorStyle, setIndicatorStyle] = useState<{
    width: number;
    left: number;
  }>({ width: 0, left: 0 });
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const navRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const [notificationType, setNotificationType] = useState<"unread" | "all">("unread");
  const userId = localStorage.getItem(StorageEnum.USER_ID);
  const [notifications, setNotifications] = useState<IResponseGetNotifications>();
  const [searchText, setSearchText] = useState<string>("");
  const { jobs } = useAppSelector(
    (state) => state.job
  );

  const getNotifications = useCallback(async () => {
    try {
      const response = await accountApi.getNotifications(userId || '');
      setNotifications(response.data.data);
    } catch (error) {
      console.log('error fetching notifications', error);
    }
  }, [userId]);

  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  const updateIndicatorPosition = useCallback((screenName: string) => {
    const activeIndex = sidebarList.findIndex(
      (item) => item.name === screenName
    );

    if (activeIndex !== -1 && navRefs.current[activeIndex]) {
      const activeElement = navRefs.current[activeIndex];
      if (activeElement) {
        const { offsetWidth, offsetLeft } = activeElement;
        if (offsetWidth > 0) {
          setIndicatorStyle({
            width: offsetWidth,
            left: offsetLeft,
          });

          if (!isInitialized) {
            setIsInitialized(true);
          }
        }
      }
    }
  }, [isInitialized]);

  useEffect(() => {
    const matchedScreen = sidebarList.find(
      (screen) => location.pathname.indexOf(screen.url) > -1
    );
    if (matchedScreen) {
      setActiveScreen(matchedScreen.name);
    }
  }, [location]);

  useEffect(() => {
    dispatch(jobActions.setShowAddJobModal(false));
  }, [location.pathname, dispatch]);

  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      updateIndicatorPosition(activeScreen);
    }, 100);

    return () => clearTimeout(timer);
  }, [activeScreen, updateIndicatorPosition]);

  useEffect(() => {
    const handleResize = () => {
      if (isInitialized) {
        updateIndicatorPosition(activeScreen);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeScreen, isInitialized, updateIndicatorPosition]);

  useEffect(() => {
    if (!isInitialized) {
      const checkAndUpdate = () => {
        const activeIndex = sidebarList.findIndex(
          (item) => item.name === activeScreen
        );

        if (activeIndex !== -1 && navRefs.current[activeIndex]) {
          const activeElement = navRefs.current[activeIndex];
          if (activeElement && activeElement.offsetWidth > 0) {
            updateIndicatorPosition(activeScreen);
          } else {
            setTimeout(checkAndUpdate, 50);
          }
        }
      };

      checkAndUpdate();
    }
  }, [activeScreen, isInitialized, updateIndicatorPosition]);

  const handleNavigateDrawer = (url: string) => {
    navigate(url);
  };

  const handleLogout = () => {
    dispatch(authActions.handleLogout());
    localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
    setIsUserMenuOpen(false);
    navigate(adminRouteAbsolute.login, { replace: true });
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    if (isNotificationOpen) setIsNotificationOpen(false);
  };

  const toggleNotification = () => {
    setIsNotificationOpen(!isNotificationOpen);
    if (isUserMenuOpen) setIsUserMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setIsUserMenuOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(target)
      ) {
        setIsNotificationOpen(false);
      }
      if (
        searchRef.current &&
        !searchRef.current.contains(target)
      ) {
        setIsSearchOpen(false);
      }
    };

    if (isUserMenuOpen || isNotificationOpen || isSearchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen, isNotificationOpen, isSearchOpen]);

  const handleNotificationType = (type: "unread" | "all") => {
    setNotificationType(type);
  };

  const getIconNotification = (type: string) => {
    switch (type) {
      case "NOTE":
        return <NoteNotificationIcon className={cx("icon-notification")} />;
      default:
        return <NoteNotificationIcon className={cx("icon-notification")} />;
    }
  };

  const handleNotificationClick = async (notification: INotifications) => {
    try {
      const response = await accountApi.markAsRead(userId || '', notification.id, { readAt: new Date().toISOString() });
      if (response.data) {
        await getNotifications();
      }

      if (notification.deeplink) {
        const deeplinkPath = notification.deeplink.startsWith('/')
          ? notification.deeplink
          : `/${notification.deeplink}`;
        const navigationPath = `/admin${deeplinkPath}`;
        navigate(navigationPath);
        setIsNotificationOpen(false);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const getTimeAgo = (dateString: string): string => {
    if (!dateString) return '';

    const createdDate = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - createdDate.getTime();

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) {
      return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }
    if (months > 0) {
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
    if (weeks > 0) {
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    }
    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
    if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    return 'Just now';
  };

  const filteredNotifications = notifications?.responses.filter((notification) => {
    if (notificationType === "unread") {
      return !notification.recipients?.some(recipient => recipient.readAt);
    }
    return true;
  }) || [];

  const handleCancelAddJob = () => {
    dispatch(jobActions.setShowAddJobModal(false));
  };

  const handleSubmitAddJob = async (data: any) => {
    try {
      const result = await dispatch(handleCreateAddJob(data)).unwrap();
      notify("success", "Add Job successful!");
      clearAddJobDraft();
      dispatch(jobActions.setShowAddJobModal(false));
      dispatch(jobActions.changeNeedRefreshData(true));
      if (result?.id) {
        navigate(adminRouteAbsolute.job.detail(result.id));
      }
    } catch (error: any) {
      notify("error", `Add Job failed: ${error?.response?.data?.message || error?.message || "Failed to add job"}`);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    const fetchJobs = async () => {
      await dispatch(handleGetJobs({
        textSearch: searchText,
      }));
    };
    fetchJobs();
  }, [dispatch, searchText]);

  const handleSearchResultClick = (jobId: string) => {
    navigate(adminRouteAbsolute.job.detail(jobId));
    setIsSearchOpen(false);
    setSearchText('');
  };

  return (
    <div className={cx("header-container")}>
      {/* Logo */}
      <BaseImage src={logo} className={cx("header-logo")} />

      {/* Navigation Menu - Center */}
      <nav className={cx("header-nav")}>

        {sidebarList.map((item, index) => (
          <button
            key={index}
            className={cx("nav-button", {
              "item-active": activeScreen === item.name,
            })}
            onClick={() => handleNavigateDrawer(item.url)}
          >
            {activeScreen === item.name ? item.activeIcon : item.icon}
            <p className={cx("nav-text", { "nav-text-active": activeScreen === item.name })}>{t(`sidebar.${item.name}`)}</p>
          </button>
        ))}
      </nav>

      {/* Search Bar & User Info */}
      <div className={cx("header-info")}>
        <div className={cx("search-container")} ref={searchRef}>
          <div className={cx("search")}>
            <SearchIcon className={cx("search-icon")} />
            <input
              type="text"
              placeholder="Search jobs, customers, phone"
              className={cx("search-input")}
              onChange={handleSearchChange}
              value={searchText}
              onFocus={() => setIsSearchOpen(true)}
            />
          </div>

          {isSearchOpen && (
            <>
              <div
                className={cx("dropdown-backdrop")}
                onClick={() => setIsSearchOpen(false)}
              />
              <div className={cx("search-dropdown")}>
                <h3 className={cx("search-dropdown-title")}>Search for jobs by title or contact.</h3>

                <div className={cx("result-container")}>
                  {jobs.length > 0 ? (
                    jobs.map((job) => (
                      <div key={job.id} className={cx("result-item")} onClick={() => handleSearchResultClick(job.id)}>
                        <h3 className={cx("result-item-title")}>{job.title}</h3>
                        <p className={cx("result-item-contact")}>Contact: {job.primaryCustomerName} {job.secondaryCustomerName ? `and ${job.secondaryCustomerName}` : ''}</p>
                      </div>
                    ))
                  ) : (
                    <div className={cx("result-empty")}>
                      <p>No results found</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <PrimaryButton
          title="Add"
          icon={<PlusIcon className={cx("create-icon")} />}
          iconPosition="left"
          onClick={() => {
            // Open Add Job modal
            dispatch(jobActions.setShowAddJobModal(true));
          }}
          className={cx("create-button")}
        />

        <div className={cx("notification-container")} ref={notificationRef}>
          <button
            className={cx("button", { "button-active": isNotificationOpen })}
            onClick={toggleNotification}
          >
            <NotificationIcon className={cx("notification-icon")} />
            {(notifications?.unreadCount ?? 0) > 0 && (
              <span className={cx("notification-badge")}>
                {(notifications?.unreadCount ?? 0) > 99 ? "99+" : (notifications?.unreadCount ?? 0)}
              </span>
            )}
          </button>

          {isNotificationOpen && (
            <>
              <div
                className={cx("dropdown-backdrop")}
                onClick={() => setIsNotificationOpen(false)}
              />
              <div className={cx("notification-dropdown")}>
                <h3 className={cx("notification-dropdown-title")}>Notifications</h3>
                <div className={cx("notification-actions")}>
                  <PrimaryButton
                    title="Unread"
                    onClick={() => handleNotificationType("unread")}
                    className={cx("notification-action-button", { "notification-action-button-active": notificationType === "unread" })}

                  />
                  <PrimaryButton
                    title="All"
                    onClick={() => handleNotificationType("all")}
                    className={cx("notification-action-button", { "notification-action-button-active": notificationType === "all" })}
                  />
                </div>

                <div className={cx("notification-list")}>
                  {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notification) => {
                      const isUnread = !notification.recipients?.some(recipient => recipient.readAt);
                      return (
                        <div
                          key={notification.id}
                          className={cx("notification-item", { "notification-item-unread": isUnread })}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className={cx("icon-notification")}>
                            {getIconNotification(notification.targetType || '')}
                          </div>
                          <div className={cx("notification-content")}>
                            <div className={cx("notification-content-header")}>
                              <p className={cx("notification-title")}>{notification.title}</p>
                              {isUnread && (
                                <div className={cx("marked-as-read")}></div>
                              )}
                            </div>
                            <p className={cx("notification-time")}>{getTimeAgo(notification.createdAt || '')}</p>
                            <p className={cx("notification-body")}>
                              {typeof notification.body === "string"
                                ? notification.body.replace(/^@/, "")
                                : notification.body}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className={cx("notification-empty")}>
                      <p>No {notificationType === "unread" ? "unread" : ""} notifications</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={cx("user-menu-container")} ref={userMenuRef}>
          <button
            className={cx("button", { "button-active": isUserMenuOpen })}
            onClick={toggleUserMenu}
          >
            <BaseImage src={logo} className={cx("user-avatar")} />
          </button>

          {isUserMenuOpen && (
            <>
              <div
                className={cx("dropdown-backdrop")}
                onClick={() => setIsUserMenuOpen(false)}
              />
              <div className={cx("user-dropdown")}>
                <button
                  className={cx("dropdown-item", "dropdown-item-logout")}
                  onClick={handleLogout}
                >
                  <LogOutIcon className={cx("logout-icon")} />
                  <span>{t("logOut")}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showAddJobModal && (
        <AddJobModal
          isOpen={showAddJobModal}
          onCancel={handleCancelAddJob}
          onSubmit={handleSubmitAddJob}
        />
      )}
    </div>
  );
}
